import React, { useState, useEffect } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { C, S } from '../../utils/styles';
import { useToast } from '../Toast';
import HelpSection from './HelpSection';

export default function EmployerPlanRulesEditor() {
  const { config, updateConfig } = useConfig();
  const showToast = useToast();
  const [draft, setDraft] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (config) {
      setDraft({
        // Enrollment Eligibility
        waitingPeriodDays: config.employerPlanRules?.waitingPeriodDays || 60,
        minWeeklyHours: config.employerPlanRules?.minWeeklyHours || 20,
        eligibleClasses: config.employerPlanRules?.eligibleClasses || {
          fullTimeW2: true,
          partTimeW2: true,
          seasonal: false,
          union: false,
        },
        // Non-Discrimination
        hceSalaryThreshold: config.employerPlanRules?.hceSalaryThreshold || 155000,
        keyEmployeeOwnershipThreshold: config.employerPlanRules?.keyEmployeeOwnershipThreshold || 5,
        maxKeyEmployeeBenefitConcentration: config.employerPlanRules?.maxKeyEmployeeBenefitConcentration || 25,
        // Wage Floor
        federalMinimumWage: config.payrollConstants?.minWageHourly || 7.25,
        annualHoursForMinWage: config.engineSettings?.annualHoursForMinWage || 2080,
      });
    }
  }, [config]);

  const handleChange = (field, value) => {
    setDraft(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleClassChange = (classType, value) => {
    setDraft(prev => ({
      ...prev,
      eligibleClasses: {
        ...prev.eligibleClasses,
        [classType]: value
      }
    }));
    setHasChanges(true);
  };

  const save = async () => {
    try {
      await updateConfig({
        employerPlanRules: {
          waitingPeriodDays: draft.waitingPeriodDays,
          minWeeklyHours: draft.minWeeklyHours,
          eligibleClasses: draft.eligibleClasses,
          hceSalaryThreshold: draft.hceSalaryThreshold,
          keyEmployeeOwnershipThreshold: draft.keyEmployeeOwnershipThreshold,
          maxKeyEmployeeBenefitConcentration: draft.maxKeyEmployeeBenefitConcentration,
        },
        payrollConstants: {
          ...config.payrollConstants,
          minWageHourly: draft.federalMinimumWage,
        },
        engineSettings: {
          ...config.engineSettings,
          annualHoursForMinWage: draft.annualHoursForMinWage,
        }
      });
      setHasChanges(false);
      showToast('Employer plan rules saved successfully', 'success');
    } catch (err) {
      showToast('Failed to save employer plan rules', 'error');
    }
  };

  const cancel = () => {
    setDraft({
      waitingPeriodDays: config.employerPlanRules?.waitingPeriodDays || 60,
      minWeeklyHours: config.employerPlanRules?.minWeeklyHours || 20,
      eligibleClasses: config.employerPlanRules?.eligibleClasses || {
        fullTimeW2: true,
        partTimeW2: true,
        seasonal: false,
        union: false,
      },
      hceSalaryThreshold: config.employerPlanRules?.hceSalaryThreshold || 155000,
      keyEmployeeOwnershipThreshold: config.employerPlanRules?.keyEmployeeOwnershipThreshold || 5,
      maxKeyEmployeeBenefitConcentration: config.employerPlanRules?.maxKeyEmployeeBenefitConcentration || 25,
      federalMinimumWage: config.payrollConstants?.minWageHourly || 7.25,
      annualHoursForMinWage: config.engineSettings?.annualHoursForMinWage || 2080,
    });
    setHasChanges(false);
  };

  if (!draft) return <div>Loading...</div>;

  const helpSections = [
    {
      heading: 'What are Employer Plan Rules?',
      content: 'Default eligibility requirements applied to all new employer clients. These define who can participate in the Section 125 plan, compliance thresholds, and wage floors. Individual employers can override these in their client record if needed (e.g., California employer with $15.50 min wage).'
    },
    {
      heading: 'Enrollment Eligibility',
      content: 'Core requirements an employee must meet to participate:',
      impacts: [
        'Waiting Period: Days from hire date before eligible (0, 30, 60, or 90 days)',
        'Minimum Weekly Hours: Part-time threshold (typically 20-30 hours/week)',
        'Eligible Classes: Which worker types qualify (W-2 full-time, part-time, seasonal, union)'
      ]
    },
    {
      heading: 'Non-Discrimination Testing',
      content: 'IRS Section 125 compliance rules to prevent plans from disproportionately benefiting highly-paid employees:',
      impacts: [
        'HCE Salary Threshold: $155,000+ earners classified as Highly Compensated Employees (IRS-indexed)',
        'Key Employee Ownership: 5%+ owners face stricter benefit caps',
        'Benefit Concentration Limit: Max 25% of total benefits can go to Key Employees (IRS rule)'
      ]
    },
    {
      heading: 'Wage Floor',
      content: 'Minimum wage constraints for payroll deduction viability:',
      impacts: [
        'Federal Minimum: $7.25/hour baseline (updated when Congress changes it)',
        'Annual Hours: 2,080 hours = standard full-time (40 hrs/week × 52 weeks)',
        'State/Local Overrides: Set higher minimums per employer (e.g., CA $15.50, NYC $16.00)',
        'Calculation: Used to determine if an employee\'s salary can support the premium deduction'
      ]
    },
    {
      heading: 'Why This Matters',
      content: (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li><strong>Legal Compliance:</strong> IRS audits Section 125 plans for discrimination — failing these tests can disqualify the entire plan</li>
          <li><strong>Workforce Analysis:</strong> Eligibility rules determine who appears in Workforce tab calculations</li>
          <li><strong>Break-Even Math:</strong> Wage floors prevent recommending plans that would violate minimum wage laws after deductions</li>
          <li><strong>Client Onboarding:</strong> New employers inherit these defaults, streamlining setup</li>
        </ul>
      )
    },
    {
      heading: 'Best Practices',
      content: (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li>Keep waiting period ≤60 days for competitive enrollment rates</li>
          <li>Exclude seasonal workers unless stable year-round workforce</li>
          <li>Update HCE threshold annually when IRS publishes new limits</li>
          <li>Monitor state minimum wage changes quarterly</li>
        </ul>
      )
    }
  ];

  return (
    <div style={S.card}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: C.text }}>Employer Plan Rules</h2>
      <p style={{ fontSize: 13, color: C.textMid, marginBottom: 20 }}>Configure default eligibility rules applied to all new clients. Individual employers can override these in their client record.</p>

      <HelpSection title="How do Employer Plan Rules work?" sections={helpSections} />

      {/* Enrollment Eligibility */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={S.heading}>Enrollment Eligibility</h3>
        
        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Waiting Period (days)</label>
          <p style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>Calendar days from hire date before employee becomes eligible</p>
          <select 
            style={S.select}
            value={draft.waitingPeriodDays}
            onChange={e => handleChange('waitingPeriodDays', parseInt(e.target.value))}
          >
            <option value="0">Waived (0 days)</option>
            <option value="30">30 days</option>
            <option value="60">60 days</option>
            <option value="90">90 days</option>
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Minimum Weekly Hours</label>
          <p style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>Minimum average weekly hours required for eligibility</p>
          <input 
            type="number"
            style={{ ...S.input, width: 120 }}
            value={draft.minWeeklyHours}
            onChange={e => handleChange('minWeeklyHours', parseFloat(e.target.value) || 0)}
            min="0"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Eligible Employment Classes</label>
          <p style={{ fontSize: 12, color: C.textMid, marginBottom: 8 }}>Which worker classifications can participate</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={draft.eligibleClasses.fullTimeW2}
                onChange={e => handleClassChange('fullTimeW2', e.target.checked)}
              />
              Full-Time W-2 Employees
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={draft.eligibleClasses.partTimeW2}
                onChange={e => handleClassChange('partTimeW2', e.target.checked)}
              />
              Part-Time W-2 Employees
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={draft.eligibleClasses.seasonal}
                onChange={e => handleClassChange('seasonal', e.target.checked)}
              />
              Seasonal / Temporary Employees
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={draft.eligibleClasses.union}
                onChange={e => handleClassChange('union', e.target.checked)}
              />
              Union Employees (CBA-covered)
            </label>
          </div>
        </div>
      </div>

      {/* Non-Discrimination */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={S.heading}>Non-Discrimination</h3>
        
        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>HCE Salary Threshold</label>
          <p style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>Annual compensation threshold for Highly Compensated Employee classification (IRS-indexed annually)</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>$</span>
            <input 
              type="number"
              style={{ ...S.input, width: 140 }}
              value={draft.hceSalaryThreshold}
              onChange={e => handleChange('hceSalaryThreshold', parseFloat(e.target.value) || 0)}
              min="0"
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Key Employee Ownership Threshold</label>
          <p style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>Ownership percentage for Key Employee classification</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input 
              type="number"
              style={{ ...S.input, width: 100 }}
              value={draft.keyEmployeeOwnershipThreshold}
              onChange={e => handleChange('keyEmployeeOwnershipThreshold', parseFloat(e.target.value) || 0)}
              min="0"
              max="100"
              step="0.1"
            />
            <span style={{ fontSize: 14, fontWeight: 600 }}>%</span>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Max Key Employee Benefit Concentration</label>
          <p style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>Maximum % of total benefits going to Key Employees (IRC §125)</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input 
              type="number"
              style={{ ...S.input, width: 100 }}
              value={draft.maxKeyEmployeeBenefitConcentration}
              onChange={e => handleChange('maxKeyEmployeeBenefitConcentration', parseFloat(e.target.value) || 0)}
              min="0"
              max="100"
            />
            <span style={{ fontSize: 14, fontWeight: 600 }}>%</span>
          </div>
        </div>
      </div>

      {/* Wage Floor */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={S.heading}>Wage Floor</h3>
        
        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Federal Minimum Wage</label>
          <p style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>Used for payroll deduction viability floor</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>$</span>
            <input 
              type="number"
              style={{ ...S.input, width: 100 }}
              value={draft.federalMinimumWage}
              onChange={e => handleChange('federalMinimumWage', parseFloat(e.target.value) || 0)}
              min="0"
              step="0.01"
            />
            <span style={{ fontSize: 13, color: C.textMid }}>/hour</span>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Annual Hours (for wage calculation)</label>
          <p style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>Standard full-time annual hours</p>
          <input 
            type="number"
            style={{ ...S.input, width: 120 }}
            value={draft.annualHoursForMinWage}
            onChange={e => handleChange('annualHoursForMinWage', parseFloat(e.target.value) || 0)}
            min="0"
          />
        </div>

        <div style={{ 
          padding: 12, 
          background: C.accentBg, 
          border: `1px solid ${C.accent}`,
          borderRadius: 8,
          fontSize: 12,
          color: C.textMid
        }}>
          <strong>Note:</strong> Individual employers can set a higher local minimum wage in their overrides (e.g., $15.50 for California).
        </div>
      </div>

      {/* Save/Cancel */}
      {hasChanges && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={cancel} style={S.buttonSecondary}>Cancel</button>
          <button data-testid="save-employer-rules-btn" onClick={save} style={S.buttonPrimary}>Save Changes</button>
        </div>
      )}
    </div>
  );
}
